/**
 * Motion dice (Options → Dice Type → Shake/Fling Dice (3D) or Popple (3D)): every roll plays in a
 * full-screen wooden tray in 3D, over the table. With Show 3D Enemy Rolls off, only your own rolls
 * do (`motionFor`); the rest use the classic dice.
 *
 * - **Popple:** your dice sit under a bubble (a dome) in the middle of the board. The first time,
 *   the camera starts at a three-quarter view and pans up to overhead (`introPan`), then the
 *   screen says to click the bubble. Pressing it pops it and the game flings the dice in a random
 *   direction (`popThrow`); no shake or hold. A die that lands badly is under a bubble again.
 *
 * - **Your roll** (Roll, Roll for War, Ghost Roll): the button opens the tray with your dice in
 *   your hand (`useMotionArm`). Two ways to throw, both always on:
 *   - **Shake** (no need to touch the screen): shake the phone like dice in a cup; each swing
 *     rattles them with a burst of clacks and a buzz (`rattle`, `shakeBuzz`). After two full
 *     shakes (`shakeMinShakes`) a final hard flick throws, or, if the Dice Lab says so, just
 *     stopping (`ShakeDetector`, `shakeRelease`). The throw is read as a flick (`readMotion`).
 *   - **Hold and flick:** hold the screen: the dice gather under your finger; shake the phone and
 *     they rattle. Flick the phone and let go: the dice fly the way and as hard as the phone
 *     moved (`readMotion`; up toward the sky lobs, down at the floor drives). A quick swipe
 *     across the screen throws too: whichever of the flick and the swipe is harder decides
 *     (`stronger`). While you hold the screen, shaking never throws on its own.
 *   Without a motion sensor (desktop, or permission denied) only the swipe (`readSwipe`). The
 *   throw goes to the server with the roll (`DiceToss`); the server rolls as always.
 * - **Every roll** (yours, other players', bots', ghosts'): when the roll comes in, the throw is
 *   simulated (`engine.simulate`) and the dice relabelled to land on the values rolled
 *   (`labelsFor`). Rolls without a throw (bots, classic dice players) get one made from the roll's
 *   `seq` (`tossFromSeq`). It lasts `rollMs` (`useRollFx` hides the table's dice meanwhile), then
 *   the tray fades away and the dice show on the table.
 * - **A bad landing** (a die not lying flat on the floor, e.g. on or leaning on another die: `Recording.bad`):
 *   the other dice stay where they landed and that die is thrown again, the same value still to
 *   come up. Your roll: it goes back in your hand and you throw it again, as before (hold, shake,
 *   flick or swipe). Anyone else's: it is thrown again for them, the same way on every screen.
 *   The tray holds the roll open meanwhile (`holdMotionRoll`), so the table waits for it.
 * Shake/Fling shows only the dice being thrown: no held dice, no hold mat. Popple's tray is glossy
 * plastic, the bubble sits up the screen and the hold mat runs along the bottom with the
 * thrower's held dice on it (`ArmSpec.held`, `RollDetails.held`); every roll in it, anyone's,
 * plays under the bubble. One roll plays at a time: any
 * that come in meanwhile (or while your dice are in your hand) wait their turn, yours first.
 *
 * The engine (three.js, cannon-es) loads only for players on motion dice. If it fails, the
 * screen goes back to classic dice (`give3dUp`).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { t, tn, type DeckColor, type DiceToss, type GameView, type RollView } from '@kadice/shared';
import {
  holdMotionRoll,
  motionDice,
  motionFor,
  motionQuickMs,
  motionRollMs,
  poppleDice,
  releaseMotionRoll,
  rollMs,
  setMotionDice,
  setMotionViewer,
} from '../rolling';
import { anim } from '../speed';
import { MotionTracker, requestMotion } from './motion';
import { clack, rattle, shakeBuzz, thud, unlockSfx } from './sfx';
import type { ShakeEvent } from './shake';
import * as sim from './sim';
import { give3dUp } from './support';
import {
  newSeed,
  popThrow,
  readMotion,
  readSwipe,
  rng,
  stronger,
  tossFromSeq,
  tossOfThrow,
  type HandThrow,
} from './toss';
import { tuning } from './tuning';
import type * as EngineModule from './engine';
import type { DieEnd, Pose, TrayScene } from './engine';

type Engine = typeof EngineModule;

/** Popple: the intro pan from a three-quarter view to overhead, once per visit to the site. */
const INTRO_MS = 1500;
let introSeen = false;

/** How long the tray takes to fade away at the end of a throw. */
const FADE_MS = 300;
/*
 * Timing from `tuning()` (Dice Lab): `holdMs`, the part of a throw's time after the dice stop
 * (they sit there to be read); `rethrowMs`, how long a die thrown again plays (before Fast
 * Animations); `rethrowPauseMs`, how long a bad landing's die glows red under the big notice
 * before it is thrown again (someone else's roll; yours goes back in your hand after
 * `myRethrowPauseMs`), so a re-roll never looks like cheating, not shortened by Fast Animations;
 * `maxRethrows`, after that many a bad landing stands.
 */
/** A throw's flight is never shorter than this, whatever the timing values. */
const MIN_FLIGHT_MS = 600;
/** How long a throw flies (s) in a roll `full` ms long: the rest is `holdMs` and the fade. */
const flightSeconds = (full: number): number => Math.max(MIN_FLIGHT_MS, full - tuning().holdMs - FADE_MS) / 1000;
/** Fast Animations: physics seconds per real second for a roll `full` ms long (the same throw, played faster). */
const rateOf = (full: number): number => full / Math.max(1, anim(full));

/** A roll you are about to throw: what your Roll button hands the tray. */
export interface ArmSpec {
  /** Dice to throw. */
  count: number;
  /** A roll that plays short (a ghost roll: `quickMs`), so your throw is worked out at that length. */
  quick?: boolean;
  /** Popple: the dice you are holding (their values), shown on the hold mat. */
  held?: number[];
  /** Send the roll with the throw; resolves false if the server refused it (the tray closes). */
  send: (toss: DiceToss | undefined) => Promise<boolean>;
}

type Arm = (spec: ArmSpec) => void;
const ArmContext = createContext<Arm | null>(null);

/** Your Roll buttons: with motion dice on, open the tray to throw (null: send the roll as usual). */
export const useMotionArm = (): Arm | null => useContext(ArmContext);

/** What a roll threw: its fresh values and the thrower's deck. */
interface RollDetails {
  values: number[];
  color: DeckColor | null;
  name: string;
  /** The dice the thrower held, not rolled (Popple's hold mat). */
  held?: number[];
}

/** The dice a roll threw, from the view it came in with (null if they can't be found: no tray for it). */
function rollDetails(view: GameView, roll: RollView): RollDetails | null {
  const p = view.players.find((x) => x.id === roll.playerId);
  if (!p) return null;
  const base = { color: p.color, name: p.name };
  if (roll.ghost) {
    const g = view.lastGhost;
    return g && g.playerId === roll.playerId ? { ...base, values: [...g.dice] } : null;
  }
  if (roll.war) {
    let rolls = view.war?.rolls[roll.playerId];
    if (!rolls || rolls.length !== roll.count) rolls = view.lastWar?.rolls[roll.playerId];
    return rolls && rolls.length === roll.count ? { ...base, values: [...rolls] } : null;
  }
  const turn = view.turn;
  if (!turn || turn.playerId !== roll.playerId) return null;
  if (turn.ghost) return { ...base, values: [...turn.pool] };
  const from = turn.pool.length - turn.rolled;
  return turn.rolled === roll.count ? { ...base, values: turn.pool.slice(from), held: turn.pool.slice(0, from) } : null;
}

/** Your roll, a die landed badly: those dice are in your hand to throw again. */
interface Rethrow {
  count: number;
  seed: number;
  throwIt: (toss: DiceToss, start: Pose[]) => void;
}

/** A roll playing in the tray. */
export interface Playing extends RollDetails {
  roll: RollView;
  toss: DiceToss;
  mine: boolean;
  /** Total time on screen (`rollMs`). */
  ms: number;
  /** Your throw: where the dice left your hand. */
  start?: Pose[];
}

/**
 * The tray and the context your Roll buttons use. Wraps the table; renders nothing extra with
 * classic dice. `live`: the game as the server sent it (rolls are caught as they come in).
 */
export function MotionDice({ live, haptics, children }: { live: GameView; haptics: boolean; children: ReactNode }) {
  const on = motionDice();
  const me = live.you.id;
  setMotionViewer(me);
  const [armed, setArming] = useState<(ArmSpec & { key: string; seed: number }) | null>(null);
  // The roll playing, the rolls waiting their turn, and the last roll seen (rolls from before
  // mount aren't replayed). One roll at a time: a roll that comes in while another plays, or
  // while your dice are in your hand (or your throw is on its way to the server), waits, and the
  // table holds its dice hidden until it has played (`holdMotionRoll`). Yours goes first.
  const [rolls, setRolls] = useState<{ seen: number; playing: Playing | null; queue: Playing[] }>({
    seen: live.lastRoll?.seq ?? 0,
    playing: null,
    queue: [],
  });
  // Where your dice left your hand (your throw starts from there).
  const [thrown, setThrown] = useState<{ poses: Pose[]; toss: DiceToss } | null>(null);
  const myColor = live.players.find((p) => p.id === me)?.color ?? null;

  // What your roll is for: if this changes before your roll comes in (turn over, war settled),
  // the throw is no longer wanted.
  const armKey = `${live.phase}:${live.round}:${live.sequence}:${live.turn?.playerId ?? ''}:${live.war?.round ?? ''}`;
  let arming = armed && armed.key === armKey ? armed : null;

  // A roll came in (as `useRollFx` sees it: in the same render, so both clocks start together).
  const roll = live.lastRoll;
  let current = rolls;
  if (roll && roll.seq > rolls.seen) {
    const details = on && motionFor(roll) ? rollDetails(live, roll) : null;
    const mine = roll.playerId === me;
    const own = mine ? thrown : null;
    const next: Playing | null = details && {
      ...details,
      roll,
      toss: roll.toss ?? own?.toss ?? tossFromSeq(roll.seq, roll.playerId),
      mine,
      ms: rollMs(roll),
      start: own?.poses,
    };
    if (!next) current = { ...rolls, seen: roll.seq };
    else if (rolls.playing || (!mine && (arming || rolls.queue.length > 0))) {
      holdMotionRoll(roll.seq);
      current = { ...rolls, seen: roll.seq, queue: mine ? [next, ...rolls.queue] : [...rolls.queue, next] };
    } else current = { ...rolls, seen: roll.seq, playing: next };
    setRolls(current);
    if (mine) {
      arming = null;
      setArming(null);
      setThrown(null);
    }
  }
  if (!on && current.queue.length > 0) {
    // Motion dice turned off: the table stops waiting on the rolls left.
    current.queue.forEach((p) => releaseMotionRoll(p.roll.seq));
    current = { ...current, queue: [] };
    setRolls(current);
  }
  if (current.queue.length > 0 && !current.playing && !arming) {
    // The tray is free and your dice aren't in your hand: the next roll waiting plays.
    current = { ...current, playing: current.queue[0]!, queue: current.queue.slice(1) };
    setRolls(current);
  }
  const playing = current.playing;
  // Left the game with rolls waiting: the table stops waiting on them.
  const queued = useRef<Playing[]>([]);
  useEffect(() => {
    queued.current = current.queue;
  });
  useEffect(() => () => queued.current.forEach((p) => releaseMotionRoll(p.roll.seq)), []);

  const arm = useCallback<Arm>(
    (spec) => {
      // Inside the button's click: iPhone asks for motion access here (it must be from a tap).
      void requestMotion();
      unlockSfx();
      setArming({ ...spec, key: armKey, seed: newSeed() });
    },
    [armKey],
  );

  return (
    <ArmContext.Provider value={on ? arm : null}>
      {children}
      {on &&
        createPortal(
          <Tray
            arming={arming}
            playing={playing}
            color={myColor}
            colors={live.players.map((p) => p.color)}
            popple={poppleDice()}
            haptics={haptics}
            onCancel={() => setArming(null)}
            onThrown={setThrown}
            onFailed={() => setArming(null)}
            onDone={(seq) => setRolls((r) => (r.playing && r.playing.roll.seq === seq ? { ...r, playing: null } : r))}
          />,
          document.body,
        )}
    </ArmContext.Provider>
  );
}

let enginePromise: Promise<Engine> | null = null;
const loadEngine = () => (enginePromise ??= import('./engine'));

/** A throw (or a re-throw) played out: where each die ended. For the Dice Lab. */
export interface ThrowReport {
  seq: number;
  /** 0: the throw; 1 on: re-throws of bad landings. */
  attempt: number;
  ends: DieEnd[];
}

/**
 * The full-screen tray (portalled to the body). Hidden while nothing is thrown. The Dice Lab
 * uses it too: `onReport` hears every throw, `rethrowBad` false leaves bad landings where
 * they are (glowing red), not thrown again, and `stay` keeps the tray open on the landed dice
 * (`onDone` still fires) with `actions` (its buttons) along the bottom. `onClose` shows the ✕
 * (top right, as in a game) while the tray is open, throws or not, and is what it calls; without
 * it the ✕ shows only while your dice are in your hand (`onCancel`), and is gone once a throw is
 * made (your dice leave your hand, or a roll plays).
 */
export function Tray({
  arming,
  playing,
  color,
  colors = [],
  popple = false,
  haptics,
  onCancel,
  onThrown,
  onFailed,
  onDone,
  onReport,
  rethrowBad = true,
  stay = false,
  actions,
  onClose,
}: {
  arming: (ArmSpec & { seed: number }) | null;
  playing: Playing | null;
  color: DeckColor | null;
  /** Every deck that may roll in the tray: their dice are made ready up front (`TrayScene.prepare`). */
  colors?: readonly (DeckColor | null)[];
  /** Popple: dice under a bubble you press (see the top of this file), not held and thrown. */
  popple?: boolean;
  haptics: boolean;
  onCancel: () => void;
  onThrown: (t: { poses: Pose[]; toss: DiceToss; read: HandThrow }) => void;
  onFailed: () => void;
  onDone: (seq: number) => void;
  onReport?: (report: ThrowReport) => void;
  rethrowBad?: boolean;
  stay?: boolean;
  actions?: ReactNode;
  onClose?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<TrayScene | null>(null);
  const engineRef = useRef<Engine | null>(null);
  const [ready, setReady] = useState(false);
  const [fading, setFading] = useState(false);
  // While your dice are in your hand: held down, and whether the sensor works (swipe if not).
  const [holding, setHolding] = useState(false);
  const [sent, setSent] = useState(false);
  // Popple: the intro pan is still going (the bubble can't be pressed yet).
  const [introing, setIntroing] = useState(false);
  // Your roll landed badly: dice back in your hand to throw again (`playing` stays on).
  const [rethrowing, setRethrowing] = useState<Rethrow | null>(null);
  // A bad landing: how many dice, and whether the big notice is up (before the re-throw).
  const [again, setAgain] = useState<{ count: number; notice: boolean } | null>(null);
  const [tracker] = useState(() => new MotionTracker());
  const [sensor, setSensor] = useState(false);
  const pointer = useRef<{ t: number; x: number; y: number }[]>([]);
  const shake = useRef(0);
  const visible = !!playing || !!arming;
  const phase = playing ? 'play' : arming ? 'hand' : 'idle';
  // Dice in your hand: before your roll, or thrown again after a bad landing.
  const inHand = phase === 'hand' || !!rethrowing;
  // Read in the loops below without restarting them.
  const hapticsRef = useRef(haptics);
  const holdingRef = useRef(false);
  // Shaking without holding the screen: hears the tracker's swings and release (`onShake`, below).
  const shakeRef = useRef<(ev: ShakeEvent) => void>(() => {});
  useEffect(() => {
    hapticsRef.current = haptics;
    holdingRef.current = holding;
  });
  // Load the engine and art up front, so the first roll is ready.
  useEffect(() => {
    let gone = false;
    loadEngine()
      .then(async (engine) => {
        if (gone || !canvasRef.current) return;
        const scene = new engine.TrayScene(canvasRef.current);
        sim.warmUp();
        await scene.ready();
        if (gone) return scene.dispose();
        scene.onHit = (h) => {
          clack(h.hit, h.wood);
          if (hapticsRef.current) thud(h.hit);
        };
        engineRef.current = engine;
        sceneRef.current = scene;
        setReady(true);
      })
      .catch(() => {
        // No 3D here after all: classic dice from now on.
        give3dUp();
        setMotionDice(false);
        onFailed();
      });
    return () => {
      gone = true;
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every deck's dice drawn and on the graphics card before its first throw (yours first), so
  // no throw starts with a hitch while they are made.
  const tints = [color, ...colors].map((c) => c ?? '').join();
  useEffect(() => {
    const scene = sceneRef.current;
    const engine = engineRef.current;
    if (!ready || !scene || !engine) return;
    scene.prepare([...new Set([color, ...colors].map((c) => engine.tintOf(c)))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, tints]);

  // Popple's plastic tray, bubble and hold mat, or the wooden tray.
  useEffect(() => {
    if (ready) sceneRef.current?.setPopple(popple);
  }, [ready, popple]);

  // Fit the canvas to the screen.
  useEffect(() => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!ready || !scene || !canvas || !visible) return;
    const fit = () => scene.resize(canvas.clientWidth, canvas.clientHeight);
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [ready, visible]);

  // Your dice in your hand, before your roll.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!ready || !scene || phase !== 'hand' || !arming) return;
    const tint = engineRef.current!.tintOf(color);
    setSent(false);
    if (!popple) {
      scene.showHand(arming.count, tint, arming.seed);
      return;
    }
    const first = !introSeen;
    introSeen = true;
    scene.showBubbleHand(arming.count, tint, arming.seed, first, INTRO_MS);
    scene.setHeld(arming.held ?? [], color);
    if (!first) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the intro is a timed phase of this arming
    setIntroing(true);
    const done = setTimeout(() => setIntroing(false), INTRO_MS);
    return () => {
      clearTimeout(done);
      setIntroing(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, phase, arming, color]);

  // While dice are in your hand: the phone's motion (or your drag) rattles them.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!ready || !scene || !inHand) return;
    setHolding(false);
    shake.current = 0;
    if (popple) {
      // Under the bubble the dice just jostle; no sensor needed.
      scene.shake = 0.3;
      return () => {
        scene.shake = 0;
      };
    }
    tracker.start((ev) => shakeRef.current(ev));
    // The sensor counts once it gives a real reading.
    const check = setInterval(() => setSensor(tracker.live), 250);
    let raf = 0;
    const tick = () => {
      // Shake: the phone's motion while you hold the dice (or how fast you drag them, without a sensor).
      const level = tracker.live ? Math.min(1, tracker.energy / 16) : dragSpeed(pointer.current) / 2.5;
      const target = holdingRef.current || tracker.shake.active(performance.now()) ? level : 0;
      shake.current = Math.max(target, shake.current * 0.9);
      scene.shake = shake.current;
      if (shake.current > 0.15 && Math.random() < shake.current * 0.35) {
        clack(shake.current * 0.6, false);
        if (hapticsRef.current) thud(shake.current * 0.5);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(check);
      tracker.stop();
    };
  }, [ready, inHand, tracker, popple]);

  // A throw playing.
  useEffect(() => {
    const scene = sceneRef.current;
    const engine = engineRef.current;
    if (!ready || !scene || !engine || !playing) return;
    const { roll, values, toss, mine, ms } = playing;
    // `useRollFx` started its clock in this same commit.
    const at = performance.now();
    const tu = tuning();
    const full = roll.animate !== false ? motionRollMs() : motionQuickMs();
    const rate = rateOf(full);
    const seconds = flightSeconds(full);
    const tint = engine.tintOf(playing.color);
    const start = mine && playing.start?.length === values.length ? playing.start : undefined;
    const pick = rng(toss.s ^ 0x9e3779b9);
    // Anyone else's tray starts on its empty board, not on the last throw's dice. Your own throw
    // is already flying from your hand (`start`), so it is left as it is.
    if (!start) scene.clearDice();
    // Popple: every roll plays under the bubble, the thrower's held dice on the mat.
    if (popple) {
      scene.showBubble();
      scene.setHeld(playing.held ?? [], playing.color);
    }
    const dome = popple ? scene.dome() : null;
    setFading(false);
    setAgain(null);
    let gone = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const wait = (ms: number) => new Promise<void>((resolve) => timers.push(setTimeout(resolve, ms)));
    // Fade away `left` ms from now, then hand the table its dice.
    const finish = (left: number) => {
      if (stay) {
        timers.push(
          setTimeout(() => {
            releaseMotionRoll(roll.seq);
            onDone(roll.seq);
          }, left),
        );
        return;
      }
      timers.push(setTimeout(() => setFading(true), Math.max(0, left - FADE_MS)));
      timers.push(
        setTimeout(() => {
          scene.stop();
          setFading(false);
          setAgain(null);
          releaseMotionRoll(roll.seq);
          onDone(roll.seq);
        }, left),
      );
    };
    void (async () => {
      // Worked out off the page; your own throw usually already is (`up`).
      let rec = await sim.simulate({ toss, count: values.length, size: scene.size, seconds, start, dome });
      if (gone) return;
      let labels = rec.top.map((face, i) => engine.labelsFor(face, values[i]!, pick()));
      // Your throw is already flying (since you let go): it just gets its faces now.
      if (scene.playing(rec)) scene.relabel(labels, tint);
      else scene.play(rec, labels, tint, rate);
      const flown = scene.playedMs();
      onReport?.({ seq: roll.seq, attempt: 0, ends: rec.ends });
      if (rec.bad.length === 0) return finish(Math.max(0, at + ms - performance.now()));
      if (!rethrowBad) {
        // Dice Lab, re-rolls off: the bad dice just glow where they lie.
        const bad = rec.bad;
        timers.push(setTimeout(() => scene.flagDice(bad), Math.max(0, (seconds * 1000) / rate - flown)));
        return finish(Math.max(0, at + ms - performance.now()));
      }
      // A bad landing: the table waits while those dice are thrown again.
      holdMotionRoll(roll.seq);
      await wait(Math.max(0, (seconds * 1000) / rate - flown));
      for (let n = 1; n <= tu.maxRethrows && rec.bad.length > 0; n++) {
        if (gone) return;
        const which = rec.bad;
        let next: { toss: DiceToss; start?: Pose[] };
        // Show it: the die glows, a big notice names the thrower, then the re-throw.
        scene.flagDice(which);
        setAgain({ count: which.length, notice: true });
        await wait(mine ? tu.myRethrowPauseMs : tu.rethrowPauseMs);
        if (gone) return;
        setAgain({ count: which.length, notice: false });
        // The re-throw gets an empty board: the dice that landed well come off it (small and
        // see-through in the corner) so they cannot be hit, and are back when it lands.
        scene.setAway(rec.top.map((_, i) => i).filter((i) => !which.includes(i)));
        if (mine) {
          const seed = newSeed();
          scene.showRethrowHand(which, tint, seed);
          setSent(false);
          next = await new Promise((resolve) =>
            setRethrowing({ count: which.length, seed, throwIt: (toss, start) => resolve({ toss, start }) }),
          );
          setRethrowing(null);
        } else {
          next = { toss: tossFromSeq(roll.seq + n * 7919, roll.playerId) };
        }
        if (gone) return;
        const secs = Math.max(MIN_FLIGHT_MS, tu.rethrowMs) / 1000;
        rec = await sim.rethrow({
          rec,
          which,
          toss: next.toss,
          size: scene.size,
          seconds: secs,
          start: next.start,
          dome,
        });
        if (gone) return;
        onReport?.({ seq: roll.seq, attempt: n, ends: rec.ends });
        labels = labels.map((l, i) => (which.includes(i) ? engine.labelsFor(rec.top[i]!, values[i]!, pick()) : l));
        // Your re-throw has been flying since you let go: it carries on from there.
        scene.play(rec, labels, tint, rate);
        await wait(Math.max(0, (secs * 1000) / rate - scene.playedMs()));
      }
      if (!gone) finish((tu.holdMs + FADE_MS) / rate);
    })();
    return () => {
      gone = true;
      timers.forEach(clearTimeout);
      setRethrowing(null);
      releaseMotionRoll(roll.seq);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, playing]);

  // Nothing to show: stop drawing.
  useEffect(() => {
    if (!visible) sceneRef.current?.stop();
  }, [visible]);

  const toFloor = (e: React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return (
      sceneRef.current?.floorAt((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height) ?? null
    );
  };

  /** Popple: pressing the bubble pops it and the game flings the dice. */
  const pop = (e: React.PointerEvent) => {
    // Press the bubble: it pops and the game flings the dice.
    const throwing = rethrowing ?? arming;
    const rect = e.currentTarget.getBoundingClientRect();
    const scene = sceneRef.current;
    if (
      introing ||
      !throwing ||
      !scene?.bubbleHit((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height)
    )
      return;
    clack(0.9, false);
    if (hapticsRef.current) thud(0.9);
    swallowNextClick();
    throwWith(popThrow(throwing.seed));
  };

  const down = (e: React.PointerEvent) => {
    if (!inHand || sent) return;
    if (popple) return pop(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    pointer.current = [{ t: e.timeStamp, x: e.clientX, y: e.clientY }];
    setHolding(true);
    if (sceneRef.current) sceneRef.current.holdAt = toFloor(e);
  };
  const move = (e: React.PointerEvent) => {
    if (!holding) return;
    const now = performance.now();
    pointer.current.push({ t: now, x: e.clientX, y: e.clientY });
    while (pointer.current.length > 2 && pointer.current[0]!.t < now - 200) pointer.current.shift();
    if (sceneRef.current) sceneRef.current.holdAt = toFloor(e);
  };
  /** Let your dice go with this throw (from `up` or a shake). */
  const throwWith = (read: HandThrow) => {
    const throwing = rethrowing ?? arming;
    if (!throwing) return;
    const scene = sceneRef.current;
    const toss = tossOfThrow(read, throwing.seed);
    const full = rethrowing
      ? playing?.roll.animate !== false
        ? motionRollMs()
        : motionQuickMs()
      : arming?.quick
        ? motionQuickMs()
        : motionRollMs();
    const rate = rateOf(full);
    // The dice fly the instant they leave your hand; the worked-out throw takes over in a few frames.
    scene?.letGo(toss, rate);
    // A bad landing thrown again: played here only (the server has the values already).
    if (rethrowing) {
      setSent(true);
      if (scene) {
        scene.holdAt = null;
        rethrowing.throwIt(toss, scene.handPoses());
      }
      return;
    }
    if (!arming) return;
    if (scene) {
      const poses = scene.handPoses();
      onThrown({ poses, toss, read });
      scene.holdAt = null;
      // Work the throw out now and play it as soon as it is, while the roll goes to the server: the
      // dice get their faces when the roll is back (same length as it will play, so it is not
      // worked out twice).
      const tint = engineRef.current?.tintOf(color) ?? '';
      void sim
        .simulate({ toss, count: arming.count, size: scene.size, seconds: flightSeconds(full), start: poses, dome: popple ? scene.dome() : null })
        .then((rec) => {
          // Still flying (not cancelled, refused, or already taken over by the roll).
          if (sceneRef.current === scene && scene.mode === 'fly') scene.play(rec, null, tint, rate);
        })
        .catch(() => {});
    }
    setSent(true);
    void arming.send(toss).then((ok) => {
      if (!ok) onFailed();
    });
  };

  const up = () => {
    const throwing = rethrowing ?? arming;
    if (!holding || !throwing) return;
    setHolding(false);
    const now = performance.now();
    // Shaken: the dice rattled for a while (more spin when they fly).
    const shook = shake.current;
    // A flick or a swipe, whichever is harder (only the swipe without a sensor).
    const { vx, vy } = dragVelocity(pointer.current, now);
    const swipe = readSwipe(vx, vy, shook, throwing.seed);
    throwWith(tracker.live ? stronger(readMotion(tracker.samples, now, shook), swipe) : swipe);
  };

  // Shaking without holding the screen: each swing rattles the dice in the cup; the release throws.
  const onShake = (ev: ShakeEvent) => {
    if (ev.kind === 'swing') {
      rattle(ev.hit);
      if (hapticsRef.current) shakeBuzz(ev.hit);
      return;
    }
    if (!inHand || holding || sent || !(rethrowing ?? arming)) return;
    throwWith({ ...readMotion(tracker.samples, ev.at, 1), from: 'shake' });
  };
  useEffect(() => {
    shakeRef.current = onShake;
  });

  const prompt = again?.notice
    ? ''
    : rethrowing
      ? sent
        ? ''
        : popple
          ? t('dice3d.popRethrow')
          : tn(sensor ? shakePrompt('dice3d.rethrowShake') : 'dice3d.rethrowSwipe', rethrowing.count)
      : playing
        ? again
          ? playing.mine
            ? tn('dice3d.rerolledMine', again.count)
            : tn('dice3d.rerolled', again.count, { name: playing.name })
          : playing.mine
            ? t('dice3d.yourRoll')
            : t('dice3d.rolls', { name: playing.name })
        : arming
          ? sent || introing
            ? ''
            : popple
              ? t('dice3d.popple')
              : sensor
                ? t(shakePrompt('dice3d.holdShake'))
                : t('dice3d.holdSwipe')
          : '';

  return (
    <div
      className={`dice-tray${visible ? ' open' : ''}${fading ? ' fading' : ''}`}
      aria-hidden={!visible}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      <canvas ref={canvasRef} className="dice-tray-canvas" />
      {visible && !ready && <p className="dice-tray-loading">{t('dice3d.loading')}</p>}
      {visible && prompt && (
        <p
          className={`dice-tray-prompt${playing && !rethrowing ? ' playing' : ''}${actions ? ' over-actions' : ''}`}
          role="status"
        >
          {prompt}
        </p>
      )}
      {playing && again?.notice && (
        <div className="dice-tray-callout" role="alert">
          <strong>{t('dice3d.stuckTitle')}</strong>
          <span>
            {playing.mine
              ? tn('dice3d.stuckMine', again.count)
              : tn('dice3d.stuck', again.count, { name: playing.name })}
          </span>
        </div>
      )}
      {visible && actions && (
        <div className={`dice-tray-actions${inHand ? ' over-hand' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
      {(onClose ? visible : !sent && !playing && arming) && (
        <button
          className="dice-tray-cancel"
          aria-label={onClose ? t('common.close') : t('dice3d.cancel')}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose ?? onCancel}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * Pressing the bubble acts on pointer-down; the tap's click comes after. If the tray has closed by
 * then (the roll came back fast), the click would land on whatever is under it, such as your card
 * (enlarging it). Eat the click that follows the press, and nothing later.
 */
function swallowNextClick(): void {
  const stop = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
    off();
  };
  const off = () => {
    window.clearTimeout(timer);
    window.removeEventListener('click', stop, true);
  };
  const timer = window.setTimeout(off, 600);
  window.addEventListener('click', stop, true);
}

/** The prompt for shaking, by how the dice are let go (`shakeRelease`: a final flick, or stopping). */
const shakePrompt = (key: string): string => (tuning().shakeRelease >= 1 ? `${key}Still` : key);

/** How fast the finger moved over the last moments (CSS px per ms) and which way. */
function dragVelocity(track: readonly { t: number; x: number; y: number }[], now: number): { vx: number; vy: number } {
  const recent = track.filter((p) => p.t >= now - 90);
  const pts = recent.length >= 2 ? recent : track.slice(-2);
  if (pts.length < 2) return { vx: 0, vy: 0 };
  const a = pts[0]!;
  const b = pts[pts.length - 1]!;
  const dt = Math.max(8, b.t - a.t);
  return { vx: (b.x - a.x) / dt, vy: (b.y - a.y) / dt };
}

const dragSpeed = (track: readonly { t: number; x: number; y: number }[]): number => {
  const { vx, vy } = dragVelocity(track, performance.now());
  return Math.hypot(vx, vy);
};
